// Old versions of the Android app must update (Admin → Settings → Android app, `app.minBuild`).
// When the app that opened this tab (src/launch.ts writes gp_app_run) is older than the oldest build
// allowed, or too old to say its build (before 32), this page takes the place of the whole site, with
// one way on: Google Play. Only in the app's own tab: browsers, including Chrome on the same phone,
// never see it. It isn't a security boundary (the API serves old apps as before); it moves people
// onto a build that runs the checks and commands the site relies on.
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { statsQuery } from '../lib/queries';
import { APP_BRIDGE_FULL } from '../lib/notify';
import { hideLaunchSplash } from '../lib/splash';
import { CoinMascot } from './Coin';
import { T, t } from '../i18n';

// Written by src/launch.ts. Repeated rather than imported, so that file stays out of this bundle.
const APP_RUN = 'gp_app_run';
const APP_RUN_EVENT = 'gp:app-run';
const APP_PACKAGE = 'com.gaintplay.makemoney.android';
const PLAY_LISTING = `https://play.google.com/store/apps/details?id=${APP_PACKAGE}`;

interface AppRun {
  build: number;
  bridge: number;
  version: string;
}

function readRun(): string | null {
  try {
    return sessionStorage.getItem(APP_RUN);
  } catch {
    return null;
  }
}

function subscribe(changed: () => void) {
  window.addEventListener(APP_RUN_EVENT, changed);
  return () => window.removeEventListener(APP_RUN_EVENT, changed);
}

function parseRun(raw: string | null): AppRun | null {
  if (!raw) return null;
  try {
    const r = JSON.parse(raw) as Partial<AppRun> | null;
    return {
      build: Number(r?.build) || 0,
      bridge: Number(r?.bridge) || 0,
      version: typeof r?.version === 'string' ? r.version.slice(0, 32) : '',
    };
  } catch {
    return null;
  }
}

/**
 * Where "Update" goes. An app that handles every command opens Google Play's in-app update, which
 * updates and restarts it in place. Older builds may not handle `update`, so they get the Play Store
 * app's listing (an intent URL: a plain https link would open Play's website inside the app).
 */
function updateHref(bridge: number): string {
  const fallback = encodeURIComponent(PLAY_LISTING);
  return bridge >= APP_BRIDGE_FULL
    ? `intent://update#Intent;scheme=gaintplay;package=${APP_PACKAGE};S.browser_fallback_url=${fallback};end`
    : `intent://details?id=${APP_PACKAGE}#Intent;scheme=market;package=com.android.vending;S.browser_fallback_url=${fallback};end`;
}

export function AppUpdateGate({ children }: { children: ReactNode }) {
  const run = parseRun(useSyncExternalStore(subscribe, readRun, () => null));
  // Public stats (cached a minute at the edge) carry the oldest build allowed. Until they arrive,
  // or if they can't be read, the site opens as usual.
  const { data } = useQuery({ ...statsQuery<{ appMinBuild?: number }>(), enabled: !!run });
  const min = data?.appMinBuild ?? 0;
  // A build that didn't say (0) is older than any minimum.
  if (!run || min <= 0 || run.build >= min) return <>{children}</>;
  return <UpdateScreen run={run} />;
}

function UpdateScreen({ run }: { run: AppRun }) {
  // i18n: {version} is the Android app's version, like 1.4.2
  const outdated = t('Version {version} is out of date', { version: run.version });
  useEffect(() => {
    // i18n: the browser tab's title while an old Android app must be updated
    document.title = t('Update GaintPlay');
    hideLaunchSplash();
  }, []);
  return (
    <main
      aria-labelledby="app-update-title"
      className="flex min-h-dvh flex-col bg-canvas px-6 pb-safe pt-safe"
    >
      <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col">
        <div className="flex flex-1 flex-col justify-center pb-8 pt-12">
          <CoinMascot size={104} rotate={-12} className="-ml-1" />
          <p className="eyebrow num mt-8">
            {run.version ? outdated : t('This version is out of date')}
          </p>
          <h1 id="app-update-title" className="large-title mt-2 text-ink">
            {t('Update GaintPlay to keep earning')}
          </h1>
          <p className="mt-3 text-pretty text-[16px] leading-relaxed text-graphite">
            {t(
              'This version of the app can’t open GaintPlay any more. The update from Google Play takes about a minute.',
            )}
          </p>
          <div className="card mt-6 flex items-start gap-3 p-4">
            <ShieldCheck
              aria-hidden="true"
              strokeWidth={1.75}
              className="mt-0.5 h-5 w-5 shrink-0 text-mint-deep"
            />
            <p className="text-[15px] leading-snug text-graphite">
              <T
                msg="<b>Your coins and cash-outs are safe.</b> They’re on your account, not on this phone."
                tags={{ b: (c) => <span className="font-semibold text-ink">{c}</span> }}
              />
            </p>
          </div>
        </div>
        <div className="pb-6">
          <a
            href={updateHref(run.bridge)}
            className="btn-primary pressable min-h-[52px] w-full text-[16px]"
          >
            {t('Update on Google Play')}
          </a>
          <p className="mt-4 text-pretty text-center text-[13px] leading-relaxed text-stone">
            {
              // i18n: {site} is the website's address, gaintplay.com
              t('Can’t update? Open {site} in your phone’s browser and sign in there.', {
                site: 'gaintplay.com',
              })
            }
          </p>
        </div>
      </div>
    </main>
  );
}
